import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronsUpDown, Languages, LogOut, Settings } from "lucide-react";

import api from "utils/api";
import { API_ENDPOINTS } from "config/api";
import { toast } from "utils/toast";
import { useUser } from "context/userContext";
import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";
import { cn } from "lib/utils";
import { usageColor } from "lib/usage-color";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { BalanceCard } from "components/credit-balance";
import { useSidebar } from "components/ui/sidebar";

function initials(fullName, email) {
  const trimmed = (fullName || "").trim();
  if (trimmed) {
    return trimmed
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  }
  return (email || "?").slice(0, 2).toUpperCase();
}

const STROKE_WIDTH = 3;

// The credit-usage ring: a neutral track plus a colored arc sweeping
// clockwise from 12 o'clock, proportional to percent used (not remaining).
// `size` is a prop rather than a constant because the collapsed (icon-only)
// sidebar rail is only 3rem wide total — the expanded-size ring wouldn't fit
// once the sidebar's own padding is accounted for.
//
// Hovering swaps the initials for `hoverLabel` (the usage percentage, or "∞"
// for unlimited) right inside the circle — no popup — via a plain CSS
// group-hover crossfade between two stacked spans, so it works with no JS
// hover-tracking state.
function UsageAvatar({ label, imageUrl, imageAlt, hoverLabel, percent, color, size }) {
  const [imageFailed, setImageFailed] = useState(false);
  const radius = (size - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = percent == null ? 0 : Math.max(0, Math.min(100, percent));
  const dash = (clamped / 100) * circumference;
  const strokeColor = percent == null ? null : color || usageColor(clamped);
  const fontSize = size <= 32 ? 9 : 11;

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  const showImage = Boolean(imageUrl) && !imageFailed;

  return (
    <span className="group/avatar relative inline-flex shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--border)" strokeWidth={STROKE_WIDTH} />
        {strokeColor ? (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={strokeColor}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference - dash}`}
            className="transition-[stroke-dasharray,stroke] duration-500 ease-out"
          />
        ) : null}
      </svg>
      <span
        aria-hidden="true"
        className="absolute overflow-hidden rounded-full bg-secondary text-foreground"
        style={{ inset: STROKE_WIDTH + 2 }}
      >
        {showImage ? (
          <img
            src={imageUrl}
            alt={imageAlt}
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
            className={cn(
              "absolute inset-0 size-full object-cover transition-opacity duration-150",
              hoverLabel && "group-hover/avatar:opacity-0"
            )}
          />
        ) : null}
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center font-semibold transition-opacity duration-150",
            showImage && "opacity-0",
            hoverLabel && "group-hover/avatar:opacity-0"
          )}
          style={{ fontSize }}
        >
          {label}
        </span>
        {hoverLabel ? (
          <span
            className="absolute inset-0 flex items-center justify-center font-semibold opacity-0 transition-opacity duration-150 group-hover/avatar:opacity-100"
            style={{ fontSize: fontSize - 1 }}
          >
            {hoverLabel}
          </span>
        ) : null}
      </span>
    </span>
  );
}

/** Profile shortcut pinned to the bottom of the sidebar. Opens an account menu; doesn't navigate anywhere on its own. */
export default function SidebarProfile() {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const { language, setLanguage } = useLanguage();
  const tr = translations[language].sidebarProfile;
  const { state, setOpen, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed";

  const [subscription, setSubscription] = useState(undefined);
  const [balance, setBalance] = useState(null);
  // Controlled so the Upgrade button (a plain Button, not a DropdownMenuItem)
  // can close the menu itself before navigating — Radix only auto-closes on
  // an actual menu item's own select.
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api
      .get(API_ENDPOINTS.SUBSCRIPTION.ME)
      .then(({ data }) => {
        if (!cancelled) setSubscription(data.subscription);
      })
      .catch(() => {
        if (!cancelled) setSubscription(null);
      });
    api
      .get(API_ENDPOINTS.CREDIT.BALANCE)
      .then(({ data }) => {
        if (!cancelled) setBalance(data);
      })
      .catch(() => {
        if (!cancelled) setBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user) return null;

  async function handleSignOut() {
    await logout();
    toast.success(tr.signedOut);
  }

  const plan = subscription?.plan;
  const isUnlimited = plan?.is_unlimited ?? false;
  // The subscription's own frozen grant, not the plan's current definition —
  // this is "how much this subscription actually gave you," which is the
  // right denominator even if the plan's terms changed since purchase.
  const totalSeconds = subscription?.credit_seconds_at_purchase ?? plan?.credit_seconds ?? null;

  let percentUsed = null;
  if (!isUnlimited && totalSeconds && balance) {
    const used = Math.max(0, totalSeconds - balance.seconds);
    percentUsed = Math.min(100, (used / totalSeconds) * 100);
  }

  function handleSettings() {
    setMenuOpen(false);
    if (isMobile) {
      setOpenMobile(false);
    } else {
      setOpen(false);
    }
    navigate("/settings");
  }

  const name = user.full_name || user.email;
  const planLabel = plan ? tr.planSuffix(plan.name) : tr.noActivePlan;
  const hoverLabel = isUnlimited ? "∞" : percentUsed != null ? `${Math.round(percentUsed)}%` : null;

  return (
    // Not a Link yet — there's nowhere for this to go for now. The whole row
    // is the dropdown trigger, so clicking it anywhere opens the menu above
    // it, and clicking again (Radix's default toggle behavior) closes it.
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        {/* asChild clones Radix's onClick/onKeyDown onto whatever element is
            given it, but not role/tabIndex — a <div> isn't natively
            focusable or announced as a control the way the <button> this
            replaced was, so both are added explicitly here. */}
        <div
          role="button"
          tabIndex={0}
          className={cn(
            "flex cursor-pointer items-center gap-2 rounded-md transition-colors hover:bg-accent",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            // The collapsed rail is 3rem wide total, with the sidebar footer's
            // own p-2 already eating into that — no room left for this row to
            // add its own horizontal padding on top without clipping the ring.
            collapsed ? "justify-center py-1" : "p-2"
          )}
        >
          <UsageAvatar
            label={initials(user.full_name, user.email)}
            imageUrl={user.avatar_url}
            imageAlt={name}
            hoverLabel={hoverLabel}
            percent={isUnlimited ? 100 : percentUsed}
            color={isUnlimited ? "rgb(59,130,246)" : undefined}
            size={collapsed ? 32 : 40}
          />

          {!collapsed ? (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-foreground">{name}</span>
                <span className="block truncate text-xs text-muted-foreground">{planLabel}</span>
              </span>

              {/* Decorative only — the row itself is the click target, not this icon. */}
              <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </>
          ) : null}
        </div>
      </DropdownMenuTrigger>

      {/* Opens upward (this widget sits at the very bottom of the sidebar).
          Deliberately wider than the trigger row itself — matching the
          trigger's width made the menu cramped, especially the balance
          card's plan name and progress bar. */}
      <DropdownMenuContent side="top" align="start" className="w-64 p-2">
        <BalanceCard
          plan={plan}
          isUnlimited={isUnlimited}
          totalMinutes={totalSeconds != null ? totalSeconds / 60 : null}
          remainingMinutes={balance ? balance.minutes : null}
          percentUsed={percentUsed}
        />

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleSettings}>
          <Settings />
          {tr.settings}
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages />
            {translations[language].ui.language}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {/* Each language is labeled in its own script, not translated
                into the currently-selected one — the standard convention
                (iOS/Android do the same) so a reader who can't read the
                current UI language can still find their own in the list. */}
            <DropdownMenuRadioGroup value={language} onValueChange={setLanguage}>
              <DropdownMenuRadioItem value="en">English</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="ar">العربية</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut />
          {tr.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
